import Grid from '@mui/material/Grid';
import { type GridProps } from '@mui/material/Grid';
import { FormInput } from 'components/ui/form/FormInput/index.js';
import { FormTextArea } from 'components/ui/form/FormTextArea/index.js';
import type {
  Control,
  DeepMap,
  DeepPartial,
  FieldError,
  FieldValues,
  UseFormClearErrors,
  UseFormResetField,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';

import type {
  FieldRenderer,
  FormItemConfiguration,
} from '../../form/GenericForm/index.js';
import { FileUpload } from '../FileUpload/index.js';
import { FormAutocomplete } from '../FormAutocomplete/index.js';
import { FormCheckbox } from '../FormCheckbox/index.js';
import { FormCheckboxes } from '../FormCheckboxes/index.js';
import { FormDatePicker } from '../FormDatePicker/index.js';
import { FormSelect } from '../FormSelect/index.js';
import { RadioGroupForm } from '../RadioGroupForm/index.js';

export interface SingleColumnFormProps<
  FormValues extends FieldValues = Record<string, any>,
> {
  fields: FormItemConfiguration;
  control: Control<FormValues>;
  errors: DeepMap<DeepPartial<FormValues>, FieldError>;
  clearErrors: UseFormClearErrors<FormValues>;
  setError: UseFormSetError<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  resetField: UseFormResetField<FormValues>;
  wrapperProps?: GridProps;
}

const defaultRender: FieldRenderer = ({
  config,
  control,
  clearErrors,
  errorExists,
  errorText,
  setError,
  resetField,
}) =>
  (config.select && (
    <FormSelect
      field={config}
      control={control}
      errorExists={errorExists}
      errorText={errorText}
    />
  ))
  || (config.textarea && (
    <FormTextArea
      field={config}
      control={control}
    />
  ))
  || (config.autocomplete && <FormAutocomplete field={config} control={control} />)
  || (config.radio && <RadioGroupForm field={config} control={control} />)
  || (config.datePicker && <FormDatePicker field={config} control={control} />)
  || (config.checkbox && <FormCheckbox field={config} control={control} />)
  || (config.checkboxes && <FormCheckboxes field={config} control={control} />)
  || (config.fileUpload && (
    <FileUpload
      field={config}
      control={control}
      errorExists={errorExists}
      errorText={errorText}
      setError={setError}
      resetField={resetField}
      clearErrors={clearErrors}
    />
  )) || (
    <FormInput
      control={control}
      field={config}
    />
  );

export const SingleColumnForm = ({
  fields,
  control,
  setError,
  setValue,
  clearErrors,
  errors,
  resetField,
  wrapperProps,
}: SingleColumnFormProps) => {
  return (
    <Grid container rowSpacing={2} columnSpacing={1} {...wrapperProps}>
      {fields.map((field) => {
        const { render, config } = field;

        const errorExists = Boolean(errors[config.name]);
        const errorText = errors[config.name]?.message;

        return (
          <Grid size={{ xs: 12 }} key={config.name} {...field?.wrapperProps}>
            {(render ?? defaultRender)({
              control,
              config,
              clearErrors,
              errorExists,
              errorText,
              resetField,
              setError,
              setValue,
            })}
          </Grid>
        );
      })}
    </Grid>
  );
};
